import React, { useState } from 'react'
import { useScrollLock } from '@src/hooks/useScrollLock'
import { useAuth } from '@src/context/AuthContext'
import { useCart } from '@src/context/CartContext'
import { useProduct } from '@src/hooks/queries/useProducts'
import { useCategoryImageMap } from '@src/hooks/useCategoryImageMap'
import PlaceOrderModal from '@src/features/customer/components/PlaceOrderModal'
import ProductFlyerModal from './ProductFlyerModal'
import SaleCornerBadge from '@src/components/SaleCornerBadge'
import { getProductImage, handleImageError } from '@src/utils/productImages'
import styled from 'styled-components'
import {
  colors,
  GrayButton,
  PrimaryButton,
  transitions,
} from '@src/theme/sharedComponents'

interface CartDrawerProps {
  show: boolean
  onClose: () => void
}

const DrawerOverlay = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: ${({ $show }) => ($show ? 'block' : 'none')};
  z-index: 1050;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

const DrawerContainer = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  height: 100dvh;
  width: 420px;
  background: ${colors.background};
  border-left: 2px solid ${colors.primary};
  z-index: 1051;
  display: flex;
  flex-direction: column;
  transform: translateX(${({ $show }) => ($show ? '0' : '100%')});
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 576px) {
    width: 100%;
  }
`

const DrawerHeader = styled.div`
  background: ${colors.primaryDark};
  color: white;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  border-bottom: 3px solid ${colors.primary};
  font-family: 'Barlow Condensed', sans-serif;

  h5 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-weight: 700;
    font-size: 1.15rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;

    i { font-size: 1.25rem; }
  }
`

const ItemCount = styled.span`
  background: ${colors.primary};
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
`

const ClearCartButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  padding: 0.3rem 0.75rem;
  transition: ${transitions.fast};
  margin-left: auto;
  margin-right: 0.75rem;

  &:hover {
    color: white;
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.08);
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  transition: ${transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active { transform: scale(0.95); }
`

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  -webkit-overflow-scrolling: touch;
`

const CartItemCard = styled.div`
  background: white;
  border: 1px solid ${colors.borderLight};
  border-left: 3px solid ${colors.primary};
  padding: 1rem;
  margin-bottom: 0.75rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  transition: ${transitions.fast};

  &:last-child { margin-bottom: 0; }

  &:hover {
    border-left-color: ${colors.accent};
    background: color-mix(in srgb, var(--color-primary) 3%, white);
  }
`

const ItemImageWrap = styled.div`
  width: 88px;
  height: 88px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  background: ${colors.backgroundAlt};
  border: 1px solid ${colors.borderLight};
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 0.3rem;
  }

  @media (max-width: 576px) {
    width: 76px;
    height: 76px;
  }
`

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
`

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const ItemName = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${colors.textLightMode};
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: ${colors.textLightModeMuted};
  cursor: pointer;
  padding: 0.5rem;
  font-size: 1rem;
  transition: ${transitions.fast};
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: -0.5rem -0.5rem -0.5rem 0;

  &:hover {
    background: rgba(220, 53, 69, 0.08);
    color: ${colors.danger};
  }

  &:active { transform: scale(0.95); }
`

const ItemPartNumber = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.78rem;
  color: ${colors.textLightModeMuted};
  font-family: 'Barlow Condensed', sans-serif;
  letter-spacing: 0.04em;

  code {
    background: ${colors.backgroundAlt};
    padding: 0.1rem 0.35rem;
    font-size: 0.75rem;
    font-family: 'JetBrains Mono', 'Courier New', monospace;
  }
`

const ItemPricing = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`

const UnitPrice = styled.span`
  font-size: 0.82rem;
  color: ${colors.textLightModeMuted};
`

const OriginalUnitPrice = styled.span`
  font-size: 0.76rem;
  color: ${colors.textLightModeMuted};
  text-decoration: line-through;
  opacity: 0.7;
`

const SavePill = styled.span`
  font-size: 0.66rem;
  font-weight: 700;
  font-family: 'Barlow Condensed', sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${colors.primary};
  color: #fff;
  padding: 0.1rem 0.4rem;
  white-space: nowrap;
`

const ItemTotal = styled.span`
  font-weight: 700;
  font-size: 0.95rem;
  color: ${colors.success};
`

const ItemFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  background: ${colors.backgroundAlt};
  border: 1px solid ${colors.borderLight};
  overflow: hidden;
`

const QuantityButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${colors.primary};
  transition: ${transitions.fast};

  &:hover { background: color-mix(in srgb, var(--color-primary) 12%, transparent); }
  &:active { transform: scale(0.95); }
  &:disabled { color: ${colors.textLightModeMuted}; cursor: not-allowed; }
`

const QuantityInput = styled.input`
  width: 48px;
  text-align: center;
  border: none;
  border-left: 1px solid ${colors.borderLight};
  border-right: 1px solid ${colors.borderLight};
  background: white;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${colors.textLightMode};
  padding: 0.5rem 0;

  &:focus {
    outline: none;
    background: ${colors.backgroundAlt};
  }

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const EmptyCart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${colors.textLightModeMuted};
  text-align: center;
  padding: 2rem;

  i {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    opacity: 0.25;
    color: ${colors.primary};
  }

  h6 {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 1.2rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${colors.textLightMode};
    margin: 0 0 0.5rem 0;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
`

const DrawerFooter = styled.div`
  padding: 1.25rem 1.5rem;
  border-top: 2px solid ${colors.primary};
  background: ${colors.backgroundAlt};
  flex-shrink: 0;
`

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.8rem;
  color: ${colors.textLightModeMuted};
  font-family: 'Barlow Condensed', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${colors.borderLight};

  .label {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${colors.textLightMode};
  }

  .total-amount {
    font-size: 1.6rem;
    font-weight: 700;
    font-family: 'Barlow Condensed', sans-serif;
    letter-spacing: 0.02em;
    color: ${colors.primary};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 576px) {
    flex-direction: column;
  }
`

const CreditInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  font-family: 'Barlow Condensed', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.textLightModeMuted};
  margin-bottom: 0.5rem;

  .value {
    font-weight: 600;
    color: ${colors.textLightModeMuted};
  }

  .unlimited {
    background: ${colors.success};
    color: white;
    padding: 0.1rem 0.4rem;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.06em;
  }
`

const CreditWarningBanner = styled.div`
  background: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.4);
  border-left: 3px solid ${colors.danger};
  color: ${colors.danger};
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.82rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  i {
    font-size: 1rem;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }

  .content {
    flex: 1;

    .title {
      font-family: 'Barlow Condensed', sans-serif;
      font-weight: 700;
      font-size: 0.88rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 0.2rem;
    }

    .details { font-size: 0.78rem; }
  }
`

const CartDrawer: React.FC<CartDrawerProps> = ({ show, onClose }) => {
  const { credit, user } = useAuth()
  const { items, removeItem, updateQuantity, getTotal, clearCart } = useCart()
  const [showPlaceOrder, setShowPlaceOrder] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const { data: flyerProductData } = useProduct(selectedProductId)
  const categoryImageMap = useCategoryImageMap()

  useScrollLock(show)

  const total = getTotal()

  const isUnlimited = credit?.is_unlimited ?? true
  const availableCredit = credit?.available_credit ?? null
  const exceedsCredit = !isUnlimited && availableCredit !== null && total > availableCredit
  const shortfall = exceedsCredit && availableCredit !== null ? total - availableCredit : 0

  const handleOrderSuccess = () => {
    clearCart()
    setShowPlaceOrder(false)
    onClose()
  }

  return (
    <>
      <DrawerOverlay $show={show} onClick={onClose} />
      <DrawerContainer $show={show}>
        <DrawerHeader>
          <HeaderLeft>
            <h5>
              <i className="bi bi-cart3"></i>
              Your Cart
              {items.length > 0 && <ItemCount>{items.length}</ItemCount>}
            </h5>
          </HeaderLeft>
          {items.length > 0 && (
            <ClearCartButton onClick={clearCart} aria-label="Clear cart">
              Clear All
            </ClearCartButton>
          )}
          <CloseButton onClick={onClose} aria-label="Close cart">
            <i className="bi bi-x-lg"></i>
          </CloseButton>
        </DrawerHeader>

        {items.length === 0 ? (
          <EmptyCart>
            <i className="bi bi-cart-x"></i>
            <h6>Cart Empty</h6>
            <p>Add some parts to get started</p>
          </EmptyCart>
        ) : (
          <>
            <DrawerBody>
              {items.map((item) => (
                <CartItemCard key={item.product_id}>
                  <ItemImageWrap
                    onClick={() => setSelectedProductId(item.product_id)}
                    title="View product details"
                  >
                    <img
                      src={getProductImage(item.image_url)}
                      alt={item.product_type}
                      onError={handleImageError}
                    />
                    {item.sale_price && <SaleCornerBadge />}
                  </ItemImageWrap>
                  <ItemDetails>
                    <ItemHeader>
                      <ItemName title={item.product_type}>{item.product_type}</ItemName>
                      <RemoveButton
                        onClick={() => removeItem(item.product_id)}
                        aria-label={`Remove ${item.product_type} from cart`}
                      >
                        <i className="bi bi-trash3"></i>
                      </RemoveButton>
                    </ItemHeader>
                    <ItemPartNumber>
                      Part #: <code>{item.sku}</code>
                    </ItemPartNumber>
                    <ItemPricing>
                      <UnitPrice>${item.price.toFixed(2)} each</UnitPrice>
                      {item.sale_price && (
                        <>
                          <OriginalUnitPrice>${item.sale_price.toFixed(2)}</OriginalUnitPrice>
                          <SavePill>Save {Math.round((1 - item.price / item.sale_price) * 100)}%</SavePill>
                        </>
                      )}
                      <span style={{ color: colors.textLightModeMuted }}>·</span>
                      <ItemTotal>${(item.price * item.quantity).toFixed(2)}</ItemTotal>
                    </ItemPricing>
                    <ItemFooter>
                      <QuantityControl>
                        <QuantityButton
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          −
                        </QuantityButton>
                        <QuantityInput
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            updateQuantity(item.product_id, Math.max(1, parseInt(e.target.value) || 1))
                          }
                          aria-label="Quantity"
                        />
                        <QuantityButton
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          +
                        </QuantityButton>
                      </QuantityControl>
                    </ItemFooter>
                  </ItemDetails>
                </CartItemCard>
              ))}
            </DrawerBody>

            <DrawerFooter>
              <SummaryRow>
                <span>{items.length} {items.length === 1 ? 'item' : 'items'}</span>
                <span>{items.reduce((sum, item) => sum + item.quantity, 0)} units</span>
              </SummaryRow>

              <CreditInfoRow>
                <span>Available Credit</span>
                {isUnlimited ? (
                  <span className="unlimited">Unlimited</span>
                ) : (
                  <span className="value">${availableCredit?.toFixed(2)}</span>
                )}
              </CreditInfoRow>

              <TotalRow>
                <span className="label">Subtotal</span>
                <span className="total-amount">${total.toFixed(2)}</span>
              </TotalRow>

              {exceedsCredit && (
                <CreditWarningBanner>
                  <i className="bi bi-exclamation-octagon-fill"></i>
                  <div className="content">
                    <div className="title">Exceeds Credit Limit</div>
                    <div className="details">
                      Cart is ${shortfall.toFixed(2)} over your available credit.
                      Remove items or contact your account manager.
                    </div>
                  </div>
                </CreditWarningBanner>
              )}

              <ButtonGroup>
                <GrayButton onClick={onClose}>Continue Shopping</GrayButton>
                <PrimaryButton onClick={() => setShowPlaceOrder(true)}>
                  <i className="bi bi-arrow-right-circle-fill"></i>
                  Checkout
                </PrimaryButton>
              </ButtonGroup>
            </DrawerFooter>
          </>
        )}
      </DrawerContainer>

      {showPlaceOrder && (
        <PlaceOrderModal
          show={showPlaceOrder}
          onClose={() => setShowPlaceOrder(false)}
          onSuccess={handleOrderSuccess}
        />
      )}

      {flyerProductData?.product && (
        <ProductFlyerModal
          product={flyerProductData.product}
          onClose={() => setSelectedProductId(null)}
          isAuthenticated={!!user}
          isAdmin={user?.is_admin ?? false}
          categoryImageMap={categoryImageMap}
        />
      )}
    </>
  )
}

export default CartDrawer
